import { PageIcon } from "@/components/PageIcon";
import React, { useState, useMemo } from "react";
import RouteMapLeaflet from "@/components/RouteMapLeaflet";
import { useLiveVehicles } from "@/hooks/useLiveVehicles";
import { useRouteIncidents } from "@/hooks/useRouteIncidents";
import { fmtEventTime } from "@/lib/liveVehicles";
import { Truck, RefreshCw, Loader2, MapPin, Navigation } from "lucide-react";

function TrafficChip({ providers }) {
  const p = providers || {};
  const here = p.here ? !!p.here.enabled : null;
  if (here === null) return null;
  return (
    <span
      data-testid="traffic-health-chip"
      title={here
        ? "HERE traffic incident feed is live — ETAs reflect current disruptions"
        : "Traffic incident feed is unavailable — ETAs may not reflect live disruptions"}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-semibold ${here ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${here ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
      {here ? "Traffic feed live" : "Traffic feed down"}
    </span>
  );
}

export default function LiveTrackingPage() {
  const { vehicles, loading, available, fetchedAt, refresh } = useLiveVehicles();
  const [focus, setFocus] = useState(null);
  const moving = vehicles.filter((v) => v.moving).length;

  // Lightweight provider-health probe: scan the fleet's own positions so the
  // LIVE SOURCES panel (Traffic incidents / Events / Weather) reports status
  // even though the fleet map has no route. Memoised on vehicle count so it
  // doesn't re-scan on every position poll. Only the provider status is used.
  const scanPoints = useMemo(
    () => vehicles.filter((v) => v.lat != null && v.lng != null).map((v) => [Math.round(v.lat * 100) / 100, Math.round(v.lng * 100) / 100]),
    [vehicles.length],
  );
  const { data: scan } = useRouteIncidents(scanPoints, { enabled: scanPoints.length > 0 });

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="live-tracking-page">
      <div className="flex flex-wrap items-center gap-3 px-4 lg:px-6 py-3 border-b border-slate-100 bg-white">
        <PageIcon />
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Live Tracking</h1>
          <p className="text-xs text-slate-500">
            {available ? <>{vehicles.length} vehicles · {moving} moving{fetchedAt ? ` · updated ${fmtEventTime(fetchedAt)}` : ""}</> : "Live vehicle feed unavailable"}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <TrafficChip providers={scan?.providers} />
          <button onClick={refresh} data-testid="live-refresh" className="inline-flex items-center gap-1.5 text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-600 hover:text-emerald-600">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0">
        <RouteMapLeaflet legs={[]} testid="live-tracking-map" fitVehicles fill focusVehicleId={focus} providersOverride={scan?.providers} />

        <div className="absolute top-3 right-3 z-[500] w-72 max-h-[calc(100%-1.5rem)] flex flex-col rounded-2xl border border-slate-200 bg-white/95 backdrop-blur shadow-lg overflow-hidden" data-testid="live-vehicle-list">
          <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-800 shrink-0">Fleet ({vehicles.length})</div>
          <div className="overflow-y-auto">
            {!available && <div className="px-4 py-8 text-center text-xs text-amber-700" data-testid="live-unavailable">Live feed unavailable right now.</div>}
            {available && vehicles.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No vehicles.</div>}
            {vehicles.map((v) => (
              <button key={v.vehicleId} onClick={() => setFocus(`${v.vehicleId}#${Date.now()}`)}
                className="w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-emerald-50/60 transition-colors" data-testid={`live-list-row-${v.vehicleId}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${v.moving ? "bg-emerald-500" : "bg-slate-400"}`} />
                  <span className="text-sm font-semibold text-slate-800">{v.registration || "(no reg)"}</span>
                  <span className="ml-auto text-[11px] text-slate-500">{v.moving ? `${v.speedKph} km/h` : "Stationary"}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{v.description}</div>
                {v.lastEventDescription && <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><Navigation className="w-3 h-3 shrink-0" /> {v.lastEventDescription}{v.lastEventTime ? ` · ${fmtEventTime(v.lastEventTime)}` : ""}</div>}
                {v.locationText && <div className="text-[11px] text-slate-400 mt-1 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> <span className="line-clamp-2">{v.locationText}</span></div>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
